const { buildReserveReportSummary } = require("./reserveReceipt");

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value) =>
  `${number(value).toLocaleString("fr-FR").replace(/[\u00a0\u202f]/g, " ")} F`;

const minorMoney = (value) => money(number(value) / 100);
const signedMoney = (value, sign) => {
  const amount = number(value);
  return amount === 0 ? money(0) : `${sign}${money(Math.abs(amount))}`;
};

const dateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("fr-FR");
};

const roleLabel = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return {
    admin: "Admin",
    cashier: "Caissier",
    caissier: "Caissier",
    manager: "Manager",
    server: "Serveur",
    serveur: "Serveur",
    waiter: "Serveur",
    superadmin: "Super Admin",
  }[normalized] || value || "Équipe";
};

const buildZReportPrintHtml = ({
  report = {},
  restaurantName = "Restaurant",
  printedBy = "—",
} = {}) => {
  const row = (label, value, strong = false) => `
    <div class="row${strong ? " strong" : ""}">
      <span>${escapeHtml(label)}</span><span>${escapeHtml(value)}</span>
    </div>`;
  const section = (title, body) => `
    <section><h2>${escapeHtml(title)}</h2>${body}</section>`;
  const sales = report.sales_summary || {};
  const gross = report.gross || {};
  const net = report.net || {};
  const adjustments = report.adjustments || {};
  const cash = report.cash_life?.period || {};
  const counts = report.cash_life?.counts || {};
  const reserves = buildReserveReportSummary(report.reserves || {});
  const categories = Array.isArray(report.categories) ? report.categories : [];
  const payments = Array.isArray(report.payment_methods) ? report.payment_methods : [];
  const cashiers = Array.isArray(report.cashiers) ? report.cashiers : [];
  const initiators = Array.isArray(report.initiators)
    ? report.initiators
    : Array.isArray(report.servers)
      ? report.servers
      : [];
  const initiatorSectionTitle = initiators.some(
    (person) => person?.attribution_kind === "steward",
  )
    ? "Serveurs"
    : "Commandes initiées par";

  const categoryRows = categories.length
    ? categories.map((category) => row(
        `${category.name || "Sans catégorie"} (${number(category.items_count)})`,
        money(category.total),
      )).join("")
    : '<p class="empty">Aucune vente</p>';

  const reserveSections = [];
  if (reserves.issuedMinor || reserves.cancelledIssueMinor) {
    const lines = reserves.lines.map((line) =>
      row(`${number(line.quantity)}x ${line.label}`, minorMoney(line.valueMinor)),
    ).join("");
    reserveSections.push(section(
      "Consignations",
      `${lines}${row("Total", minorMoney(reserves.issuedMinor), true)}` +
        (reserves.cancelledIssueMinor
          ? row("Mises en réserve annulées", minorMoney(reserves.cancelledIssueMinor))
          : ""),
    ));
  }
  if (reserves.redeemedMinor || reserves.restoredRedemptionMinor) {
    reserveSections.push(section(
      "Réserves utilisées",
      `${row("Total", minorMoney(reserves.redeemedMinor), true)}` +
        (reserves.restoredRedemptionMinor
          ? row("Montants recrédités", minorMoney(reserves.restoredRedemptionMinor))
          : ""),
    ));
  }

  const paymentRows = payments.length
    ? payments.map((payment) =>
        row(payment.name || "Autre", money(payment.amount)),
      ).join("")
    : '<p class="empty">Aucun encaissement</p>';

  const cashierRows = cashiers.length
    ? cashiers.map((cashier) =>
        row(`${cashier.name || "Caissier"} (${number(cashier.services)} ticket(s))`, money(cashier.net_total)),
      ).join("")
    : '<p class="empty">Aucun encaissement</p>';

  const initiatorRows = initiators.length
    ? initiators.map((person) =>
        row(`${person.name || "Utilisateur"} · ${roleLabel(person.role)}`, money(person.net_total)),
      ).join("")
    : '<p class="empty">Aucune commande</p>';

  const summaryRows = [
    row("Brut", money(sales.gross_sales ?? gross.total)),
    row("Remises", signedMoney(sales.total_discounts ?? adjustments.discounts?.amount, "-")),
    row("À crédit", signedMoney(sales.total_credit_sales, "-")),
    row("Réserves utilisées", signedMoney(sales.reserve_redeemed ?? reserves.redeemedMinor / 100, "-")),
    row("Net", money(sales.net_sales ?? net.total), true),
  ].join("");

  const cashRows = [
    row("Fonds initiaux", money(cash.opening_funds)),
    row("Dépôts", signedMoney(cash.deposits, "+")),
    row("Retraits", signedMoney(cash.withdrawals, "-")),
    row("Espèces encaissées", money(cash.cash_collected)),
    row("Solde théorique", money(cash.net_cash_flow), true),
    ...(number(counts.count) > 0
      ? [
          row("Comptage réel", money(counts.actual)),
          row("Écart", money(counts.difference), true),
        ]
      : []),
  ].join("");

  return `<!doctype html>
<html><head><meta charset="utf-8"/><title>Rapport Z</title><style>
*{box-sizing:border-box}body{margin:0;background:#fff;color:#000;font-family:Arial,sans-serif;font-size:11px;font-weight:600}
.ticket{width:100%;padding:7px 6px 10px}.center{text-align:center}.store{font-size:15px;font-weight:800}.title{font-size:15px;font-weight:900;margin-top:5px}
.meta,.empty{font-size:10px;margin:2px 0}.separator,section{border-top:1px solid #000;margin-top:8px;padding-top:7px}h2{font-size:11px;margin:0 0 5px;text-transform:uppercase;letter-spacing:.06em}
.row{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:8px;margin:2px 0;line-height:1.25}.row span:first-child{overflow-wrap:anywhere}.row span:last-child{white-space:nowrap;text-align:right}.strong{font-weight:900}
@page{margin:0}@media print{body{width:100%}}
</style></head><body><div class="ticket">
<div class="center"><div class="store">${escapeHtml(restaurantName)}</div><div class="title">RAPPORT Z</div>
<div class="meta">Période : ${escapeHtml(String(report.range?.start || "—"))} au ${escapeHtml(String(report.range?.end || "—"))}</div>
<div class="meta">Première vente : ${escapeHtml(dateTime(report.first_sale_at))}</div><div class="meta">Dernière vente : ${escapeHtml(dateTime(report.last_sale_at))}</div>
<div class="meta">Tiré par : ${escapeHtml(printedBy)} · ${escapeHtml(dateTime(report.generated_at))}</div></div>
${section("Détail par catégorie", categoryRows)}
${reserveSections.join("")}
${section("Résumé ventes", summaryRows)}
${section("Moyens de paiement", paymentRows)}
${section("Vie de caisse", cashRows)}
${section("Activité", [
  row("Tickets", String(number(net.ticket_count ?? gross.tickets ?? gross.services))),
  row("Tables servies", String(number(gross.tables_served))),
  row("Visiteurs", String(number(gross.visitors))),
  row("Panier moyen", money(net.average_ticket)),
  ...(net.average_per_visitor == null ? [] : [row("Moyenne par visiteur", money(net.average_per_visitor))]),
  row(`Annulations (${number(adjustments.cancellations?.count)})`, signedMoney(adjustments.cancellations?.amount, "-")),
].join(""))}
${section("Par caissier", cashierRows)}
${section(initiatorSectionTitle, initiatorRows)}
<div class="separator center meta">Powered by MOO Sync</div>
</div></body></html>`;
};

module.exports = { buildZReportPrintHtml };
